import { useState, type ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon } from "../icons.tsx";

export interface PhotoTileProps {
  name: string;
  /** Second line, e.g. "Grandmother" or "Room 3". */
  detail?: ReactNode;
  /** Enrolment photo URL; initials are shown when absent or when it fails to load. */
  src?: string;
  kind?: "adult" | "child";
  /** Makes the tile a toggle button with aria-pressed. */
  onSelect?: () => void;
  selected?: boolean;
  disabled?: boolean;
  /** Flags the tile (e.g. a restricted adult) with a visible label, not colour alone. */
  flag?: ReactNode;
  className?: string;
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? words[words.length - 1]![0] ?? "" : "";
  return (first + last).toUpperCase();
}

/** One of five calm initials tints (.pmu-tint-0…4), stable per name; never a red, in any presentation. */
export function tintFor(name: string): number {
  let h = 0;
  for (const ch of name.trim().toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % 5;
}

export function PhotoTile({ name, detail, src, kind = "adult", onSelect, selected = false, disabled, flag, className }: PhotoTileProps) {
  const [failed, setFailed] = useState(false);
  const hasPhoto = Boolean(src && !failed);
  const photo = hasPhoto
    ? <img className="pmu-photo__img" src={src} alt="" onError={() => setFailed(true)} draggable={false} />
    : <span className="pmu-photo__initials" aria-hidden="true">{initials(name)}</span>;
  const body = (
    <>
      <span className={cx("pmu-photo__frame", `pmu-photo__frame--${kind}`, !hasPhoto && `pmu-tint-${tintFor(name)}`)}>
        {photo}
        {selected && <span className="pmu-photo__check" aria-hidden="true"><Icon name="check" /></span>}
      </span>
      <span className="pmu-photo__name">{name}</span>
      {detail && <span className="pmu-photo__detail">{detail}</span>}
      {flag && <span className="pmu-photo__flag">{flag}</span>}
    </>
  );
  const classes = cx("pmu-photo", `pmu-photo--${kind}`, selected && "pmu-photo--selected", className);
  if (!onSelect) return <figure className={classes}>{body}</figure>;
  return (
    <button type="button" className={classes} aria-pressed={selected} disabled={disabled} onClick={onSelect}>
      {body}
    </button>
  );
}
